import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import ParentLayout from '../components/ParentLayout';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Plus, Trash2, Image, X, Upload, Star, StarOff, ChevronLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: 'game_day', label: 'Game Day' },
  { key: 'practice', label: 'Practice' },
  { key: 'cheer', label: 'Cheer' },
  { key: 'events', label: 'Events' },
  { key: 'community', label: 'Community' },
  { key: 'general', label: 'General' },
];

const AdminGallery = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadCategory, setUploadCategory] = useState('general');
  const [uploadCaption, setUploadCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [filterCat, setFilterCat] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/public/gallery`);
      setGallery(res.data || []);
    } catch (error) {
      console.error('Failed to fetch gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(f => f.type.startsWith('image/'));
    if (validFiles.length === 0) return;

    const newFiles = validFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      caption: '',
      category: uploadCategory,
      featured: false
    }));
    setUploadFiles(prev => [...prev, ...newFiles]);
    if (!showUploadModal) setShowUploadModal(true);
    e.target.value = '';
  };

  const removeUploadFile = (idx) => {
    setUploadFiles(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[idx].preview);
      updated.splice(idx, 1);
      return updated;
    });
  };

  const updateUploadFile = (idx, field, value) => {
    setUploadFiles(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

  const handleUpload = async () => {
    if (uploadFiles.length === 0) return;
    setUploading(true);
    setUploadProgress({ current: 0, total: uploadFiles.length });

    for (let i = 0; i < uploadFiles.length; i++) {
      const item = uploadFiles[i];
      setUploadProgress({ current: i + 1, total: uploadFiles.length });
      try {
        const base64 = await toBase64(item.file);
        await axios.post(`${API_URL}/api/gallery/upload`, {
          file_data: base64,
          filename: item.file.name,
          content_type: item.file.type,
          caption: item.caption,
          category: item.category,
          featured: item.featured
        }, { withCredentials: true });
      } catch (error) {
        console.error(`Failed to upload ${item.file.name}:`, error);
      }
    }

    // Cleanup
    uploadFiles.forEach(f => URL.revokeObjectURL(f.preview));
    setUploadFiles([]);
    setShowUploadModal(false);
    setUploading(false);
    setUploadProgress({ current: 0, total: 0 });
    fetchGallery();
  };

  const handleDelete = async (imageId) => {
    try {
      await axios.delete(`${API_URL}/api/gallery/${imageId}`, { withCredentials: true });
      setGallery(prev => prev.filter(img => img.image_id !== imageId));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Failed to delete image:', error);
    }
  };

  const toggleFeatured = async (imageId, currentFeatured) => {
    try {
      await axios.patch(`${API_URL}/api/gallery/${imageId}`, {
        featured: !currentFeatured
      }, { withCredentials: true });
      setGallery(prev => prev.map(img =>
        img.image_id === imageId ? { ...img, featured: !currentFeatured } : img
      ));
    } catch (error) {
      console.error('Failed to update featured:', error);
    }
  };

  const filtered = filterCat === 'all' ? gallery : gallery.filter(img => img.category === filterCat);

  const catCounts = CATEGORIES.reduce((acc, cat) => {
    acc[cat.key] = gallery.filter(img => img.category === cat.key).length;
    return acc;
  }, {});

  if (loading) {
    return (
      <ParentLayout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-12 bg-gray-200 rounded-lg w-1/3"></div>
            <div className="grid grid-cols-4 gap-3">
              {[1,2,3,4,5,6,7,8].map(i => <div key={i} className="aspect-square bg-gray-200 rounded-lg"></div>)}
            </div>
          </div>
        </div>
      </ParentLayout>
    );
  }

  return (
    <ParentLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-testid="admin-gallery">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin/dashboard')} className="text-gray-400 hover:text-navy transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-display text-2xl sm:text-3xl font-black text-navy uppercase tracking-tighter">Gallery Manager</h1>
              <p className="text-sm text-gray-500">{gallery.length} photo{gallery.length !== 1 ? 's' : ''} in the guest gallery</p>
            </div>
          </div>
          <Button
            onClick={() => fileInputRef.current?.click()}
            className="bg-red hover:bg-red/90 text-white font-bold uppercase tracking-wider text-xs mt-4 sm:mt-0"
            data-testid="upload-photos-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Upload Photos
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFilesSelected}
            className="hidden"
          />
        </div>

        {/* Category Filter */}
        <div className="flex gap-2 overflow-x-auto pb-3 mb-6" style={{ scrollbarWidth: 'none' }} data-testid="gallery-cat-filter">
          <button
            onClick={() => setFilterCat('all')}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all flex-shrink-0 ${
              filterCat === 'all' ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid="filter-all"
          >
            All <span className="opacity-60">{gallery.length}</span>
          </button>
          {CATEGORIES.map(cat => (
            <button
              key={cat.key}
              onClick={() => setFilterCat(cat.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all flex-shrink-0 ${
                filterCat === cat.key ? 'bg-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`filter-${cat.key}`}
            >
              {cat.label} <span className="opacity-60">{catCounts[cat.key] || 0}</span>
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        {filtered.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="gallery-admin-grid">
            {filtered.map((img) => (
              <div
                key={img.image_id}
                className="group relative aspect-square rounded-xl overflow-hidden bg-gray-100 border border-gray-200"
                data-testid={`admin-gallery-${img.image_id}`}
              >
                <img
                  src={img.url}
                  alt={img.caption || 'Gallery'}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-200">
                  <div className="absolute top-2 left-2 right-2 flex items-start justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <Badge className={`text-[10px] ${
                      img.category === 'game_day' ? 'bg-red text-white' :
                      img.category === 'cheer' ? 'bg-pink-600 text-white' :
                      img.category === 'practice' ? 'bg-light-blue text-white' :
                      img.category === 'community' ? 'bg-green-600 text-white' :
                      img.category === 'events' ? 'bg-purple-600 text-white' :
                      'bg-gray-600 text-white'
                    }`}>
                      {img.category?.replace(/_/g, ' ')}
                    </Badge>
                    <div className="flex gap-1">
                      <button
                        onClick={() => toggleFeatured(img.image_id, img.featured)}
                        className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                          img.featured ? 'bg-gold text-navy' : 'bg-white/80 text-gray-600 hover:bg-gold hover:text-navy'
                        }`}
                        title={img.featured ? 'Remove featured' : 'Set as featured'}
                        data-testid={`featured-${img.image_id}`}
                      >
                        {img.featured ? <Star className="w-3.5 h-3.5 fill-current" /> : <StarOff className="w-3.5 h-3.5" />}
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(img.image_id)}
                        className="w-7 h-7 rounded-full bg-white/80 text-red hover:bg-red hover:text-white flex items-center justify-center transition-colors"
                        title="Delete"
                        data-testid={`delete-${img.image_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  {img.caption && (
                    <div className="absolute bottom-2 left-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <p className="text-white text-xs bg-black/50 px-2 py-1 rounded">{img.caption}</p>
                    </div>
                  )}
                </div>
                {/* Featured badge always visible */}
                {img.featured && (
                  <div className="absolute top-2 right-2 group-hover:opacity-0 transition-opacity">
                    <div className="w-6 h-6 bg-gold rounded-full flex items-center justify-center">
                      <Star className="w-3 h-3 text-navy fill-current" />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Image className="w-14 h-14 mx-auto text-gray-200 mb-4" />
            <h3 className="font-display text-lg font-bold text-navy mb-1">
              {filterCat !== 'all' ? `No ${filterCat.replace(/_/g, ' ')} photos yet` : 'No photos yet'}
            </h3>
            <p className="text-sm text-gray-400 mb-4">Upload photos to display on the guest page gallery.</p>
            <Button
              onClick={() => fileInputRef.current?.click()}
              className="bg-navy hover:bg-navy/90 text-white font-bold uppercase tracking-wider text-xs"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload First Photos
            </Button>
          </div>
        )}

        {/* Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="upload-modal">
            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b">
                <div>
                  <h2 className="font-display text-xl font-black text-navy uppercase tracking-tight">Upload Photos</h2>
                  <p className="text-xs text-gray-500">{uploadFiles.length} photo{uploadFiles.length !== 1 ? 's' : ''} ready to upload</p>
                </div>
                <button
                  onClick={() => { setShowUploadModal(false); uploadFiles.forEach(f => URL.revokeObjectURL(f.preview)); setUploadFiles([]); }}
                  className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
                  disabled={uploading}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {/* Default category for new uploads */}
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Default Category:</span>
                  <select
                    value={uploadCategory}
                    onChange={(e) => setUploadCategory(e.target.value)}
                    className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 font-semibold"
                    data-testid="default-category"
                  >
                    {CATEGORIES.map(c => (
                      <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                  </select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    className="ml-auto text-xs font-bold"
                    disabled={uploading}
                    data-testid="add-more-btn"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add More
                  </Button>
                </div>

                {/* File List */}
                {uploadFiles.map((item, idx) => (
                  <div key={idx} className="flex gap-3 p-3 bg-gray-50 rounded-lg" data-testid={`upload-item-${idx}`}>
                    <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                      <img src={item.preview} alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          placeholder="Add a caption..."
                          value={item.caption}
                          onChange={(e) => updateUploadFile(idx, 'caption', e.target.value)}
                          className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-1.5"
                          disabled={uploading}
                        />
                        <button
                          onClick={() => removeUploadFile(idx)}
                          className="w-7 h-7 rounded-full bg-red/10 text-red hover:bg-red hover:text-white flex items-center justify-center transition-colors"
                          disabled={uploading}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={item.category}
                          onChange={(e) => updateUploadFile(idx, 'category', e.target.value)}
                          className="text-xs border border-gray-200 rounded-lg px-2 py-1"
                          disabled={uploading}
                        >
                          {CATEGORIES.map(c => (
                            <option key={c.key} value={c.key}>{c.label}</option>
                          ))}
                        </select>
                        <button
                          onClick={() => updateUploadFile(idx, 'featured', !item.featured)}
                          className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg border transition-colors ${
                            item.featured ? 'bg-gold/20 border-gold text-navy font-bold' : 'border-gray-200 text-gray-400 hover:border-gold'
                          }`}
                          disabled={uploading}
                        >
                          <Star className={`w-3 h-3 ${item.featured ? 'fill-current' : ''}`} />
                          Featured
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
                {uploading ? (
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-bold text-navy">Uploading {uploadProgress.current} of {uploadProgress.total}...</span>
                      <span className="text-xs text-gray-500">{Math.round((uploadProgress.current / uploadProgress.total) * 100)}%</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-navy rounded-full transition-all duration-300"
                        style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-xs text-gray-400">{uploadFiles.length} photo{uploadFiles.length !== 1 ? 's' : ''} selected</p>
                    <Button
                      onClick={handleUpload}
                      disabled={uploadFiles.length === 0}
                      className="bg-red hover:bg-red/90 text-white font-bold uppercase tracking-wider text-xs"
                      data-testid="upload-submit-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload All
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation */}
        {deleteConfirm && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="delete-confirm-modal">
            <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 text-center">
              <Trash2 className="w-10 h-10 mx-auto text-red mb-3" />
              <h3 className="font-display text-lg font-black text-navy mb-2">Delete Photo?</h3>
              <p className="text-sm text-gray-500 mb-6">This will permanently remove the photo from the guest gallery.</p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 font-bold text-xs uppercase tracking-wider"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="flex-1 bg-red hover:bg-red/90 text-white font-bold text-xs uppercase tracking-wider"
                  data-testid="confirm-delete-btn"
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ParentLayout>
  );
};

export default AdminGallery;
